import React, { useEffect, useState } from "react";
import { http, requestErrorMessage, companyLogoUrl } from "@/lib/erp";
import { useAuth } from "@/context/AuthContext";
import UsersRoles from "@/components/UsersRoles";
import QrLabelSettings from "@/components/settings/QrLabelSettings";
import SignatureSettings from "@/components/settings/SignatureSettings";
import ReminderSettings from "@/components/settings/ReminderSettings";
import BusinessDataCleanup from "@/components/settings/BusinessDataCleanup";
import { AdvancedSettings, SocialLinksSettings, ReferralSettings, SupportSettings } from "@/components/settings/OtherSettings";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Save, Building2, UserRound, Users, SlidersHorizontal, Receipt, Barcode,
  PenTool, StickyNote, Bell, Landmark, Star, Share2, UserPlus, Headphones, Upload, Trash2, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import WhatsAppSettings from "@/components/WhatsAppSettings";

const MENU = [
  { group: "Profile", items: [
    { key: "company", label: "Company Details", icon: Building2 },
    { key: "profile", label: "User Profile", icon: UserRound },
    { key: "users", label: "All Users / Roles", icon: Users },
  ]},
  { group: "General Settings", items: [
    { key: "preferences", label: "Preferences", icon: SlidersHorizontal },
    { key: "thermal", label: "Thermal Print Settings", icon: Receipt },
    { key: "barcode", label: "QR Label Settings", icon: Barcode },
    { key: "signatures", label: "Signatures", icon: PenTool },
    { key: "notes", label: "Notes & Terms", icon: StickyNote },
    { key: "reminders", label: "Auto Reminders", icon: Bell },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  ]},
  { group: "Banks and Payments", items: [
    { key: "banks", label: "Banks", icon: Landmark },
  ]},
  { group: "Others", items: [
    { key: "advanced", label: "Advanced Features", icon: Star },
    { key: "social", label: "Social Links", icon: Share2 },
    { key: "referral", label: "Referral", icon: UserPlus },
    { key: "support", label: "Support", icon: Headphones },
  ]},
];

const Field = ({ label, k, form, set, mono, type, placeholder }) => (
  <div><Label>{label}</Label><Input type={type || "text"} value={form[k] || ""} placeholder={placeholder}
    onChange={(e) => set(k, e.target.value)} className={cn("mt-1.5", mono && "font-mono")} data-testid={`settings-${k}`} /></div>
);

const SETTINGS_SECTIONS = ["company", "preferences", "notes", "banks", "thermal", "barcode", "signatures", "reminders", "social"];
const DOCUMENT_TYPES = [
  ["sales_invoice", "Sales Invoice"],
  ["purchase_invoice", "Purchase Invoice"],
  ["quotation", "Quotation"],
  ["sales_order", "Sales Order"],
  ["delivery_challan", "Delivery Challan"],
  ["credit_note", "Credit Note"],
];

const createId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export default function Settings() {
  const { user } = useAuth();
  const [active, setActive] = useState("company");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({ name: "", phone: "", email: "", password: "", current_password: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());
  const [documentType, setDocumentType] = useState("sales_invoice");
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsLoadError, setSettingsLoadError] = useState("");
  const [profileLoadError, setProfileLoadError] = useState("");

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Logo too large (max 3MB)");
    const fd = new FormData();
    fd.append("file", file);
    try {
      await http.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, has_logo: true }));
      setLogoBust(Date.now());
      toast.success("Logo uploaded");
    } catch (e) { toast.error(requestErrorMessage(e, "upload the company logo")); }
  };
  const removeLogo = async () => {
    try {
      await http.delete("/company/logo");
      setForm((f) => ({ ...f, has_logo: false }));
      toast.success("Logo removed");
    } catch (e) { toast.error(requestErrorMessage(e, "remove the company logo")); }
  };

  const loadSettings = async () => {
    setSettingsLoading(true);
    setSettingsLoadError("");
    try {
      const r = await http.get("/settings");
      const data = r.data || {};
      let banks = Array.isArray(data.banks) ? data.banks.map((bank) => ({ ...bank, id: bank.id || createId() })) : [];
      // Older company settings only have a single bank record. Keep it visible
      // and migrate it into the new collection when the settings are saved.
      if (!Object.prototype.hasOwnProperty.call(data, "banks") && (data.bank_name || data.bank_account || data.bank_ifsc)) {
        banks = [{
          id: createId(),
          bank_name: data.bank_name || "",
          branch_name: data.branch_name || "",
          account_number: data.bank_account || "",
          ifsc: data.bank_ifsc || "",
          swift_code: data.swift_code || "",
          upi_id: data.upi_id || "",
        }];
      }
      const selectedDefault = data.default_bank_id;
      setForm({
        ...data,
        banks,
        default_bank_id: banks.length
          ? (banks.some((bank) => bank.id === selectedDefault) ? selectedDefault : (banks[0]?.id || null))
          : null,
        document_texts: data.document_texts || {},
      });
    } catch (error) {
      setSettingsLoadError(requestErrorMessage(error, "load settings"));
    } finally {
      setSettingsLoading(false);
    }
  };
  useEffect(() => { loadSettings(); }, []);
  const loadProfile = async () => {
    setProfileLoadError("");
    try {
      const { data } = await http.get("/auth/me");
      setProfile({ name: data.name || "", phone: data.phone || "", email: data.email || "", password: "", current_password: "" });
    } catch (error) {
      setProfileLoadError(requestErrorMessage(error, "load your profile"));
    }
  };
  useEffect(() => {
    if (active === "profile") loadProfile();
  }, [active]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const documentTextPair = () => {
    const existing = form.document_texts?.[documentType] || {};
    return {
      notes: existing.notes ?? form.default_notes ?? "",
      terms: existing.terms ?? form.default_terms ?? "",
    };
  };
  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await http.put("/settings", form);
      const banks = Array.isArray(data.banks) ? data.banks : (form.banks || []);
      const selectedDefault = data.default_bank_id || form.default_bank_id;
      setForm({
        ...data,
        banks,
        default_bank_id: banks.length
          ? (banks.some((bank) => bank.id === selectedDefault) ? selectedDefault : (banks[0]?.id || null))
          : null,
        document_texts: data.document_texts || form.document_texts || {},
      });
      toast.success("Settings saved");
    }
    catch (e) { toast.error(requestErrorMessage(e, "save settings")); }
    finally { setSaving(false); }
  };
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = { name: profile.name, phone: profile.phone };
      if (profile.password) { payload.password = profile.password; payload.current_password = profile.current_password; }
      await http.put("/auth/profile", payload);
      toast.success("Profile updated");
      setProfile((p) => ({ ...p, password: "", current_password: "" }));
    } catch (e) { toast.error(requestErrorMessage(e, "update your profile")); }
    finally { setSavingProfile(false); }
  };
  const refreshSignature = async () => {
    try {
      const { data } = await http.get("/settings");
      setForm((current) => ({
        ...current, has_signature: data.has_signature,
        signature_updated_at: data.signature_updated_at,
      }));
    } catch (error) {
      toast.error(requestErrorMessage(error, "refresh signature settings"));
    }
  };

  const activeItem = MENU.flatMap((g) => g.items).find((i) => i.key === active);
  const showSettingsSave = SETTINGS_SECTIONS.includes(active) && active !== "whatsapp";

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6"><h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Settings</h1><p className="text-sm text-muted-foreground">Configure your company, team, printing & preferences</p></div>
      <BusinessDataCleanup />

      <div className="grid lg:grid-cols-[260px_1fr] gap-6 items-start">
        {/* Left menu */}
        <Card className="p-3 lg:sticky lg:top-4">
          <nav className="space-y-4">
            {MENU.map((g) => (
              <div key={g.group}>
                <div className="px-2 text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">{g.group}</div>
                <div className="space-y-0.5">
                  {g.items.map((it) => (
                    <button key={it.key} onClick={() => setActive(it.key)}
                      data-testid={`settings-nav-${it.key}`}
                      className={cn("w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors text-left",
                        active === it.key ? "bg-brand-50 text-brand-700 font-medium" : "text-slate-600 hover:bg-slate-100")}>
                      <it.icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{it.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </Card>

        {/* Right content */}
        <div className="space-y-4 min-w-0">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">{activeItem?.icon && <activeItem.icon className="h-5 w-5 text-brand-600" />}<h2 className="font-heading text-lg font-bold">{activeItem?.label}</h2></div>
            {showSettingsSave && <Button onClick={saveSettings} disabled={saving || settingsLoading || Boolean(settingsLoadError)} data-testid="save-settings-button"><Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save Changes"}</Button>}
          </div>
          {settingsLoading && <p role="status" className="text-sm text-muted-foreground">Loading settings…</p>}
          {settingsLoadError && <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{settingsLoadError} <Button variant="outline" size="sm" className="ml-2" onClick={loadSettings}>Retry</Button></div>}
          {profileLoadError && active === "profile" && <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{profileLoadError} <Button variant="outline" size="sm" className="ml-2" onClick={loadProfile}>Retry</Button></div>}

          {active === "company" && (
            <Card className="p-6 grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 flex items-center gap-4 pb-4 border-b">
                <div className="h-20 w-40 rounded-lg border border-dashed border-slate-300 bg-slate-50 grid place-items-center overflow-hidden shrink-0">
                  {form.has_logo
                    ? <img src={`${companyLogoUrl(form)}?t=${logoBust}`} alt="Company logo" className="max-h-full max-w-full object-contain" data-testid="company-logo-preview" />
                    : <span className="text-[11px] text-slate-400 text-center px-2">No logo</span>}
                </div>
                <div className="space-y-2">
                  <div className="text-sm font-medium">Company Logo</div>
                  <p className="text-xs text-muted-foreground max-w-xs">Prints on invoices, sales docs & packing slips. PNG/JPG, max 3MB.</p>
                  <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-brand-600 text-white cursor-pointer hover:bg-brand-700" data-testid="upload-logo-button">
                      <Upload className="h-4 w-4" /> Upload
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-file-input" />
                    </label>
                    {form.has_logo && <Button variant="outline" size="sm" onClick={removeLogo} data-testid="remove-logo-button"><Trash2 className="h-4 w-4 mr-1" /> Remove</Button>}
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2"><Field label="Company Name" k="name" form={form} set={set} /></div>
              <div className="sm:col-span-2"><Field label="Address" k="address" form={form} set={set} /></div>
              <Field label="GSTIN" k="gstin" form={form} set={set} mono />
              <Field label="PAN" k="pan" form={form} set={set} mono />
              <Field label="State" k="state" form={form} set={set} />
              <Field label="State Code" k="state_code" form={form} set={set} mono />
              <div className="sm:col-span-2"><Field label="Drug License No (DL 20B/21B)" k="dl_no" form={form} set={set} /></div>
              <Field label="Phone" k="phone" form={form} set={set} />
              <Field label="Email" k="email" form={form} set={set} />
            </Card>
          )}

          {active === "profile" && (
            <Card className="p-6 grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 flex items-center gap-3 pb-2">
                <span className="h-12 w-12 rounded-full bg-brand-100 text-brand-700 grid place-items-center font-semibold">{(profile.name || user?.name || "U")[0]}</span>
                <div><div className="font-semibold">{profile.name || user?.name}</div><div className="text-xs text-muted-foreground">{user?.role}</div></div>
              </div>
              <div><Label>Full Name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1.5" data-testid="profile-name-input" /></div>
              <div><Label>Mobile</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="mt-1.5" data-testid="profile-phone-input" /></div>
              <div><Label>Email</Label><Input value={profile.email} disabled className="mt-1.5 bg-slate-50" /></div>
              <div><Label>Current Password</Label><Input type="password" value={profile.current_password} onChange={(e) => setProfile({ ...profile, current_password: e.target.value })} placeholder="Required to change password" className="mt-1.5" data-testid="profile-current-password-input" /></div>
              <div><Label>New Password</Label><Input type="password" value={profile.password} onChange={(e) => setProfile({ ...profile, password: e.target.value })} placeholder="Leave blank to keep current" className="mt-1.5" data-testid="profile-password-input" /></div>
              <div className="sm:col-span-2"><Button onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-button"><Save className="h-4 w-4 mr-1" /> {savingProfile ? "Saving…" : "Save Profile"}</Button></div>
            </Card>
          )}

          {active === "users" && <UsersRoles />}

          {active === "preferences" && (
            <Card className="p-6 space-y-5">
              <div>
                <div className="font-heading font-semibold text-sm mb-1">Party Auto-Numbering</div>
                <p className="text-xs text-muted-foreground mb-3">New customers & vendors get a unique ID as <span className="font-mono">PREFIX-YYYYMM-0001</span> (resets monthly, per company).</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Customer ID Prefix" k="customer_prefix" form={form} set={set} mono placeholder="CUST" />
                  <Field label="Vendor ID Prefix" k="vendor_prefix" form={form} set={set} mono placeholder="VEND" />
                </div>
              </div>
              <div className="border-t pt-5">
                <div className="font-heading font-semibold text-sm mb-1">Employee Auto-Numbering</div>
                <p className="text-xs text-muted-foreground mb-3">New employees get an ID as <span className="font-mono">PREFIX-YYYY-00001</span>, automatically per company. Prefix changes apply only to new employees; existing IDs stay unchanged.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Employee ID Prefix" k="employee_prefix" form={form} set={set} mono placeholder="EMP" />
                  <div className="self-end rounded-md bg-muted px-3 py-2 text-sm" data-testid="employee-id-preview">
                    Preview: <span className="font-mono">{String(form.employee_prefix || "EMP").trim().toUpperCase() || "EMP"}-{new Date().getUTCFullYear()}-00001</span>
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Use up to 12 letters, digits, hyphens or underscores. The sequence is assigned when the employee is saved.</p>
              </div>
              <div className="border-t pt-5">
                <div className="font-heading font-semibold text-sm mb-3">Document Numbering</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Invoice Prefix" k="invoice_prefix" form={form} set={set} mono placeholder="INV" />
                </div>
              </div>
            </Card>
          )}

          {active === "notes" && (
            <Card className="p-6 grid gap-4">
              <div>
                <Label htmlFor="document-type">Document type</Label>
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger id="document-type" className="mt-1.5" data-testid="document-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOCUMENT_TYPES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="document-notes">Notes</Label>
                <Textarea id="document-notes" rows={5} value={form.document_texts?.[documentType]?.notes ?? form.default_notes ?? ""}
                  onChange={(e) => set("document_texts", {
                    ...(form.document_texts || {}),
                    [documentType]: { ...documentTextPair(), notes: e.target.value },
                  })} className="mt-1.5" data-testid="document-notes-input" />
              </div>
              <div>
                <Label htmlFor="document-terms">Terms &amp; Conditions</Label>
                <Textarea id="document-terms" rows={5} value={form.document_texts?.[documentType]?.terms ?? form.default_terms ?? ""}
                  onChange={(e) => set("document_texts", {
                    ...(form.document_texts || {}),
                    [documentType]: { ...documentTextPair(), terms: e.target.value },
                  })} className="mt-1.5" data-testid="document-terms-input" />
              </div>
              <p className="text-xs text-muted-foreground">Unconfigured document types continue to use the default notes and terms.</p>
            </Card>
          )}

          {active === "banks" && (
            <Card className="p-6 space-y-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div><h3 className="font-semibold">Company bank accounts</h3><p className="text-xs text-muted-foreground mt-1">Manage up to 20 accounts. Removing an account affects future choices only; existing bills keep their saved bank snapshot.</p></div>
                <Button type="button" variant="outline" disabled={(form.banks || []).length >= 20} onClick={() => {
                  const banks = [...(form.banks || []), { id: createId(), bank_name: "", branch_name: "", account_number: "", ifsc: "", swift_code: "", upi_id: "" }];
                  set("banks", banks);
                  if (!form.default_bank_id) set("default_bank_id", banks[banks.length - 1].id);
                }} data-testid="add-bank-button"><Landmark className="h-4 w-4 mr-1" /> Add Bank</Button>
              </div>
              {(form.banks || []).map((bank, index) => {
                const updateBank = (key, value) => set("banks", form.banks.map((item) => item.id === bank.id ? { ...item, [key]: value } : item));
                return <div key={bank.id} className="rounded-lg border p-4 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium">Bank {index + 1}</div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="radio" name="default-bank" checked={form.default_bank_id === bank.id}
                          onChange={() => set("default_bank_id", bank.id)} className="accent-brand-600" />
                        Default bank
                      </label>
                      <Button type="button" size="sm" variant="ghost" onClick={() => {
                        if (!window.confirm("Remove this bank? It will no longer be available for future choices; existing bills keep their bank snapshot.")) return;
                        const banks = form.banks.filter((item) => item.id !== bank.id);
                        set("banks", banks);
                        if (!banks.length || form.default_bank_id === bank.id) set("default_bank_id", banks[0]?.id || null);
                      }} data-testid={`remove-bank-${index}`}><Trash2 className="h-4 w-4 text-rose-600 mr-1" /> Remove</Button>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      ["bank_name", "Bank name"], ["branch_name", "Branch name"], ["account_number", "Account number"],
                      ["ifsc", "IFSC"], ["swift_code", "SWIFT code"], ["upi_id", "UPI ID"],
                    ].map(([key, label]) => <div key={key}><Label htmlFor={`bank-${index}-${key}`}>{label}</Label>
                      <Input id={`bank-${index}-${key}`} value={bank[key] || ""} onChange={(e) => updateBank(key, e.target.value)}
                        className="mt-1.5" data-testid={`bank-${index}-${key}`} /></div>)}
                  </div>
                </div>;
              })}
              {!(form.banks || []).length && <p className="text-sm text-muted-foreground border rounded-lg p-6 text-center">No bank accounts added yet.</p>}
            </Card>
          )}

          {active === "thermal" && (
            <Card className="p-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Default Print Format</Label>
                  <Select value={form.print_default_format || "a4"} onValueChange={(v) => set("print_default_format", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="settings-print_default_format"><SelectValue /></SelectTrigger>
                    <SelectContent>{[["a4", "A4"], ["a5", "A5"], ["thermal", "Thermal"]].map((o) => <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Template Style</Label>
                  <Select value={form.print_template || "classic"} onValueChange={(v) => set("print_template", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="settings-print_template"><SelectValue /></SelectTrigger>
                    <SelectContent>{[["classic", "Classic"], ["modern", "Modern"]].map((o) => <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Thermal Paper Width</Label>
                  <Select value={form.thermal_width || "80"} onValueChange={(v) => set("thermal_width", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="settings-thermal_width"><SelectValue /></SelectTrigger>
                    <SelectContent>{[["80", "80mm"], ["58", "58mm"]].map((o) => <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="font-heading font-semibold text-sm mb-3">Show on printed invoice</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {[["print_show_gstin", "Company GSTIN & PAN"], ["print_show_bank", "Bank & UPI details"], ["print_show_terms", "Terms & Conditions"], ["print_show_signature", "Authorised Signatory"], ["print_show_logo", "Company Logo"]].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={form[k] !== false} onChange={(e) => set(k, e.target.checked)} className="h-4 w-4 accent-brand-600" data-testid={`settings-${k}`} />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div><Label>Footer / Thank-you Note</Label><Input value={form.print_footer_text || ""} onChange={(e) => set("print_footer_text", e.target.value)} placeholder="e.g. Thank you for your business!" className="mt-1.5" data-testid="settings-print_footer_text" /></div>
            </Card>
          )}

          {active === "barcode" && <QrLabelSettings form={form} set={set} />}
          {active === "signatures" && <Card className="p-6"><SignatureSettings form={form} set={set} onRefresh={refreshSignature} /></Card>}
          {active === "reminders" && <ReminderSettings form={form} set={set} />}
          {active === "whatsapp" && <WhatsAppSettings />}
          {active === "advanced" && <AdvancedSettings />}
          {active === "social" && <SocialLinksSettings form={form} set={set} />}
          {active === "referral" && <ReferralSettings />}
          {active === "support" && <SupportSettings />}
        </div>
      </div>
    </div>
  );
}
